import { AvatarDropdown, AvatarName, Question, SelectLang } from '@/components';
import NotificationsBell from '@/components/RightContent/NotificationsBell';
import ForcePasswordChange from '@/components/ForcePasswordChange';
import {
  AppstoreOutlined,
  FundProjectionScreenOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';

import type { RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import authService from '@/services/AuthService';
import userConfigService from '@/services/UserConfigService';
import { getModuleForPath } from '@/config/routeModuleMap';
import { getStoredTheme, applyTheme } from '@/styles/theme';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

// Use Spanish as the global day.js locale so every date UI (incl. the AntD
// RangePicker / month pickers) shows month and weekday names in Spanish.
dayjs.locale('es');

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/login';
const googleCompletePath = '/auth/google/complete';

// Initialize theme on app startup
const initializeTheme = () => {
  const theme = getStoredTheme();
  applyTheme(theme);
};

// Call theme initialization
initializeTheme();

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  permissions?: any;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {  
  const fetchUserInfo = async (): Promise<API.CurrentUser | undefined> => {
    try {
      const CACHE_KEY = 'cached_permissions';
      const MAX_CACHE_AGE_MS = 5 * 60 * 1000; // 5 minutes

      const toBoolean = (value: any): boolean => value === true || value === 1 || value === '1';

      const normalizePermissions = (payload: any): Record<string, any> => {
        if (!payload) return {};

        let data = payload;
        if (data && typeof data === 'object' && (data as any).permissions) {
          data = (data as any).permissions;
        }

        const out: Record<string, any> = {};

        if (Array.isArray(data)) {
          data.forEach((perm: any) => {
            const moduleName = perm?.module;
            if (!moduleName) return;

            const normalized = {
              C: toBoolean(perm?.C ?? perm?.hasCreate),
              R: toBoolean(perm?.R ?? perm?.hasRead),
              U: toBoolean(perm?.U ?? perm?.hasUpdate),
              D: toBoolean(perm?.D ?? perm?.hasDelete),
            };

            out[moduleName] = {
              ...normalized,
              hasAnyPermission:
                toBoolean(perm?.hasAnyPermission) ||
                normalized.C ||
                normalized.R ||
                normalized.U ||
                normalized.D,
            };
          });

          return out;
        }

        if (data && typeof data === 'object') {
          Object.entries(data as Record<string, any>).forEach(([moduleName, perm]) => {
            const normalized = {
              C: toBoolean(perm?.C ?? perm?.hasCreate),
              R: toBoolean(perm?.R ?? perm?.hasRead),
              U: toBoolean(perm?.U ?? perm?.hasUpdate),
              D: toBoolean(perm?.D ?? perm?.hasDelete),
            };

            out[moduleName] = {
              ...normalized,
              hasAnyPermission:
                toBoolean(perm?.hasAnyPermission) ||
                normalized.C ||
                normalized.R ||
                normalized.U ||
                normalized.D,
            };
          });

          return out;
        }

        return {};
      };

      // First check if user is authenticated with our AuthService
      const isAuthenticated = await authService.isAuthenticated();
      if (!isAuthenticated) {
        localStorage.removeItem(CACHE_KEY);
        return undefined;
      }

      const userSession = authService.getCurrentSession();
      if (!userSession) {
        localStorage.removeItem(CACHE_KEY);
        return undefined;
      }

      let userPermissions: Record<string, any> = {};
      let permissionsLoadedSuccessfully = false;

      try {
        const permissionsResponse = await authService.getUserPermissions();
        const permsPayload: any = permissionsResponse?.data ?? permissionsResponse;

        userPermissions = normalizePermissions(permsPayload);
        permissionsLoadedSuccessfully = true;

        // Store in localStorage as backup but with timestamp
        const permissionsCache = {
          permissions: userPermissions,
          timestamp: Date.now(),
          userId: userSession.fk_user,
          userRole: userSession.fk_rol,
        };
        localStorage.setItem(CACHE_KEY, JSON.stringify(permissionsCache));
      } catch (permError) {
        // Try to load from cache as absolute last resort, but with strict validation
        try {
          const cachedData = localStorage.getItem(CACHE_KEY);
          if (cachedData) {
            const parsed = JSON.parse(cachedData);
            const isRecent =
              typeof parsed?.timestamp === 'number' && Date.now() - parsed.timestamp < MAX_CACHE_AGE_MS;
            const isSameUser = parsed?.userId === userSession.fk_user;
            const isSameRole = parsed?.userRole === userSession.fk_rol;

            if (isRecent && isSameUser && isSameRole && parsed?.permissions) {
              console.warn('SECURITY: Using cached permissions as fallback');
              userPermissions = normalizePermissions(parsed.permissions);
              permissionsLoadedSuccessfully = true;
            } else {
              localStorage.removeItem(CACHE_KEY);
            }
          }
        } catch (cacheError) {
          localStorage.removeItem(CACHE_KEY);
        }
      }

      // Always return user info, but mark if permissions failed to load
      const userInfo: any = {
        id: userSession.fk_user,
        name: userSession.user,
        email: userSession.email,
        cedula: userSession.cedula,
        // Default avatar (generated) - will be replaced below if user has uploaded a photo
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(userSession.user)}&background=1890ff&color=fff`,
        userid: userSession.fk_user.toString(),
        role: userSession.rol,
        fk_rol: userSession.fk_rol,
        permissions: userPermissions,
        permissionsLoaded: permissionsLoadedSuccessfully,
      };

      // Try to load an uploaded profile photo from the API. If present, use it
      try {
        const uploadedPhoto = await userConfigService.getUserPhotoDataUrl(userSession.fk_user);
        if (uploadedPhoto) {
          userInfo.avatar = uploadedPhoto;
        }
      } catch (photoErr) {
        // non-fatal: keep the generated avatar
        console.warn('Could not fetch uploaded user photo:', photoErr);
      }
      
      return userInfo;
      
    } catch (error) {
      localStorage.removeItem('cached_permissions');
      return undefined;
    }
  };

  const { location } = history;
  
  // Initialize auth service
  await authService.initialize();
  
  const isPublicRoute = location.pathname === loginPath || location.pathname === googleCompletePath;

  if (!isPublicRoute) {
    const currentUser = await fetchUserInfo();
    if (!currentUser) {
      // Redirige inmediatamente al login si no hay sesión válida
      history.push(loginPath);
      return {
        fetchUserInfo,
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    }
    return {
      fetchUserInfo,
      currentUser,
      permissions: (currentUser as any)?.permissions,
      loading: false,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
  menu: {
    defaultOpenKeys: ['/_menu/operacion'],
  },
  actionsRender: () => [
    <NotificationsBell key="notifications" />,
    <Question key="doc" />,
    <SelectLang key="SelectLang" />,
    // <GoogleConnectButton key="google-connect" />,
  ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },

    // Filtrado de menú basado en permisos
    // Additionally apply a mobile-only blacklist at render-time so the sidebar
    // doesn't show heavy admin/reporting items on small screens.
    menuDataRender: (menuData: any[]) => {
      const isClientMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
      const mobileBlacklist = [
        '/CreditNotes',
        '/OrderViewer',
        '/InvoiceReport',
        '/Invoices',
        '/responsive-demo',
        '/Admin',
      ];

      const renameByPath: Record<string, string> = {
        '/home': 'Página Inicial',
        '/Project': 'Proyectos',
        '/ExecutiveDashboard': 'Executive Dashboard',
        '/ClientKanban': 'Kanban Clientes',
        '/PotentialClients': 'Clientes Potenciales',
        '/FinancialBudgetManager': 'Presupuestos',
        '/GlobalPlanning': 'Planeación Global',
        '/GlobalPlanningV2': 'Actividades',
        '/OrderViewer': 'Pedidos CRM',
        '/Invoices': 'Facturas',
        '/CreditNotes': 'Notas crédito',
        '/CreatedOrders': 'Pedidos',
        '/ClientsManagement': 'Mis clientes',
        '/FreeClients': 'Disponibles',
        '/OrderReport': 'Reporte pedidos local',
        '/InvoiceReport': 'Reporte facturas',
        '/CRMAnalytics': 'Analytics CRM',
        '/AdministrativeAnalysis': 'Análisis Administrativo',
        '/StickerCreator': 'Crear stickers',
      };

      const allowedPaths = new Set([
        '/home',
        '/Compromisos',
        '/ExecutiveDashboard',
        '/DashboardB2B',
        '/Project',
        '/ClientKanban',
        '/PotentialClients',
        '/FinancialBudgetManager',
        '/GlobalPlanning',
        '/GlobalPlanningV2',
        '/OrderViewer',
        '/Invoices',
        '/CreditNotes',
        '/CreatedOrders',
        '/ClientsManagement',
        '/FreeClients',
        '/AdministrativeAnalysis',
        '/StockQuery',
        '/StickerCreator',
        '/Admin',
      ]);

      const menuGroups = [
        {
          key: '/_menu/tableros',
          name: 'Tableros',
          icon: <FundProjectionScreenOutlined />,
          paths: ['/DashboardB2B'],
        },
        {
          key: '/_menu/operacion',
          name: 'Operación',
          icon: <AppstoreOutlined />,
          paths: ['/ExecutiveDashboard', '/Project', '/ClientKanban', '/PotentialClients', '/FinancialBudgetManager', '/GlobalPlanning', '/GlobalPlanningV2', '/StickerCreator'],
        },
        {
          key: '/_menu/consulta',
          name: 'Consultas',
          icon: <TeamOutlined />,
          paths: ['/OrderViewer', '/Invoices', '/CreditNotes', '/CreatedOrders', '/ClientsManagement', '/FreeClients', '/StockQuery'],
        },
      ];

      const getChildren = (item: any) => {
        if (Array.isArray(item?.children)) return item.children;
        if (Array.isArray(item?.routes)) return item.routes;
        return null;
      };

      const setChildren = (item: any, children: any[] | null) => {
        if (!children) return { ...item, children: undefined };
        return { ...item, children };
      };

      const cloneItem = (item: any, overrides: any = {}) => {
        const next = { ...item, ...overrides };
        if ('routes' in next) delete next.routes;
        return next;
      };

      const filterMobile = (items: any[]): any[] => {
        if (!Array.isArray(items)) return [];

        return items
          .map((item) => {
            const children = getChildren(item);
            const nextChildren = children ? filterMobile(children) : null;
            const nextItem = children ? setChildren(item, nextChildren) : item;

            if (isClientMobile && nextItem?.path && mobileBlacklist.includes(nextItem.path)) {
              return null;
            }

            // Drop empty menu groups
            if (
              Array.isArray(nextItem?.children) &&
              nextItem.children.length === 0 &&
              !nextItem.component &&
              !nextItem.redirect
            ) {
              return null;
            }

            return nextItem;
          })
          .filter(Boolean);
      };

      const hasAccessToMenuItem = (menu: any) => {
        // Rutas sin access siempre visibles (públicas)
        if (!menu?.access) return true;

        // Solo la ruta home es siempre visible para usuarios autenticados
        if (menu.path === '/home') {
          return true;
        }

        // Para rutas protegidas, verificar permisos estrictamente
        // Primero, si la ruta tiene moduleName en su metadata, úsala
        if (menu.moduleName && initialState?.permissions) {
          const modulePerms =
            initialState.permissions[menu.moduleName] ||
            initialState.permissions[
              menu.moduleName === 'Consulta'
                ? 'Consultas'
                : menu.moduleName === 'Consultas'
                ? 'Consulta'
                : ''
            ];
          return !!(modulePerms && modulePerms.hasAnyPermission === true);
        }

        // Fallback: si existe mapping de ruta -> módulo en el mapa central, úsalo
        if (menu.path) {
          const moduleInfo = getModuleForPath(menu.path);
          if (moduleInfo && initialState?.permissions) {
            const modulePerms =
              initialState.permissions[moduleInfo.moduleName] ||
              initialState.permissions[
                moduleInfo.moduleName === 'Consulta'
                  ? 'Consultas'
                  : moduleInfo.moduleName === 'Consultas'
                  ? 'Consulta'
                  : ''
              ];
            if (!modulePerms) return false;
            // Si se definió una permission requerida, verificarla (C/R/U/D)
            if (moduleInfo.requiredPermission) {
              return !!modulePerms[moduleInfo.requiredPermission];
            }
            return !!modulePerms.hasAnyPermission;
          }
        }

        // Si la ruta tiene access pero no moduleName, DENEGAR acceso por seguridad
        if (menu.access && !menu.moduleName) {
          return false;
        }

        // Fallback: denegar acceso
        return false;
      };

      const filterByPermissions = (items: any[]): any[] => {
        if (!Array.isArray(items)) return [];

        return items
          .map((menu) => {
            const children = getChildren(menu);
            const filteredChildren = children ? filterByPermissions(children) : null;
            const nextMenu = children ? setChildren(menu, filteredChildren) : menu;

            // Si no hay usuario, solo mostrar rutas públicas
            if (!initialState?.currentUser) {
              if (!nextMenu?.access) {
                // Keep public routes, but drop empty groups
                if (Array.isArray(nextMenu?.children) && nextMenu.children.length === 0 && !nextMenu.path) {
                  return null;
                }
                return nextMenu;
              }
              return null;
            }

            // Si no hay permisos cargados, ser muy restrictivo
            if (!initialState?.permissions || Object.keys(initialState.permissions).length === 0) {
              const isAdmin = (initialState.currentUser as any)?.fk_rol === 1;
              const permissionsLoaded = (initialState.currentUser as any)?.permissionsLoaded;

              if (isAdmin && permissionsLoaded === false) {
                const allow =
                  !nextMenu.access ||
                  nextMenu.path === '/home' ||
                  nextMenu.path === '/Admin' ||
                  nextMenu.path === '/permissions-debug';

                if (!allow) return null;

                if (Array.isArray(nextMenu?.children) && nextMenu.children.length === 0 && !nextMenu.component && !nextMenu.redirect) {
                  return null;
                }

                return nextMenu;
              }

              // Para usuarios no admin sin permisos, solo rutas públicas
              return !nextMenu.access ? nextMenu : null;
            }

            const allowedSelf = hasAccessToMenuItem(nextMenu);

            // If it has visible children, keep the group even if itself doesn't have access.
            if (Array.isArray(nextMenu?.children) && nextMenu.children.length > 0) {
              return nextMenu;
            }

            return allowedSelf ? nextMenu : null;
          })
          .filter(Boolean);
      };

      const groupAndOrder = (items: any[]) => {
        if (!Array.isArray(items)) return [];

        const home = items.find((i) => i?.path === '/home') || null;
        const rest = items.filter((i) => i?.path !== '/home');
        const restWithPath = rest.filter((i) => !!i?.path && allowedPaths.has(i.path));
        const restWithoutPath = rest.filter((i) => !i?.path);

        const byPath = new Map<string, any>();
        for (const item of restWithPath) {
          if (item?.path) {
            byPath.set(item.path, item);
          }
        }

        const used = new Set<string>();
        const out: any[] = [];

        if (home) {
          const renamedHome = renameByPath['/home'] ? cloneItem(home, { name: renameByPath['/home'] }) : home;
          out.push(renamedHome);
        }

        for (const group of menuGroups) {
          const children: any[] = [];
          for (const p of group.paths) {
            const item = byPath.get(p);
            if (!item) continue;
            used.add(p);
            const renamed = renameByPath[p] ? cloneItem(item, { name: renameByPath[p] }) : item;
            children.push(renamed);
          }

          if (children.length === 0) continue;

          // If there's only one item in a group (due to permissions), don't force extra clicks.
          if (children.length === 1) {
            out.push(children[0]);
            continue;
          }

          out.push({
            name: group.name,
            icon: group.icon,
            path: group.key,
            children,
          });
        }

        // Keep anything not explicitly grouped (future-proofing / new routes)
        const leftovers = restWithPath.filter((i) => i?.path && !used.has(i.path));
        leftovers.forEach((item) => {
          if (item?.path && renameByPath[item.path]) {
            out.push(cloneItem(item, { name: renameByPath[item.path] }));
            return;
          }
          out.push(item);
        });

        restWithoutPath.forEach((item) => out.push(item));

        return out;
      };

      const mobileFiltered = filterMobile(Array.isArray(menuData) ? menuData : []);
      const permissionFiltered = filterByPermissions(mobileFiltered);
      // Additional pass: filter routes using central route->module map to guarantee
      // routes without proper module permissions are removed even if grouping logic
      // would keep their parent menu.
      const filterRoutesByModuleMap = (items: any[]): any[] => {
        if (!Array.isArray(items)) return [];
        return items
          .map((item) => {
            const children = getChildren(item);
            const nextChildren = children ? filterRoutesByModuleMap(children) : null;
            const nextItem = children ? setChildren(item, nextChildren) : item;

            if (nextItem?.path) {
              const moduleInfo = getModuleForPath(nextItem.path);
              if (moduleInfo) {
                if (isDev) {
                  console.debug('[menu filter] route -> moduleInfo', nextItem.path, moduleInfo);
                  console.debug('[menu filter] current permission keys', Object.keys(initialState?.permissions || {}));
                }
                // If no permissions loaded, only allow admins
                if (!initialState?.permissions || Object.keys(initialState.permissions).length === 0) {
                  const isAdmin = (initialState?.currentUser as any)?.fk_rol === 1;
                  if (!isAdmin) return null;
                  return nextItem;
                }

                const modulePerms =
                  initialState.permissions[moduleInfo.moduleName] ||
                  initialState.permissions[
                    moduleInfo.moduleName === 'Consulta'
                      ? 'Consultas'
                      : moduleInfo.moduleName === 'Consultas'
                      ? 'Consulta'
                      : ''
                  ];
                if (!modulePerms) return null;
                if (moduleInfo.requiredPermission) {
                  if (!modulePerms[moduleInfo.requiredPermission]) return null;
                } else if (!modulePerms.hasAnyPermission) {
                  return null;
                }
              }
            }

            // Keep items without mapped module (no central rule)
            return nextItem;
          })
          .filter(Boolean);
      };

      const moduleFiltered = filterRoutesByModuleMap(permissionFiltered);
      return groupAndOrder(moduleFiltered);
    },

    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],

    menuHeaderRender: undefined,

    childrenRender: (children) => {
      // ForcePasswordChange se pinta por encima de todo el layout: si el login
      // devolvió must_change_password, bloquea la aplicación hasta que el
      // usuario defina una contraseña nueva.
      return (
        <>
          {children}
          <ForcePasswordChange />
        </>
      );
    },
    ...initialState?.settings,
  };
};

export const request = {
  ...errorConfig,
};
